import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router";
import {
  useGetCourseCurriculumWithProgressQuery,
  useGetCourseReviewQuery,
} from "../../../app/features/courses/courses.api";
import { useQueryFilters } from "../../../hooks/useQueryFilters";
import UserLayout from "../../../layouts/user";
import CertificateView from "./_components/CertificateView";
import UserCourseCurriculum from "./_components/CourseCurriculum";
import CourseRatingModal from "./_components/CourseRatingModal";
import UserCourseContent from "./_components/UserCourseContent";

export default function CourseModule() {
  const { id: courseId } = useParams();
  const [searchParams] = useSearchParams();
  const { filters } = useQueryFilters<{ title: string }>();
  const { data } = useGetCourseReviewQuery(Number(courseId), {
    skip: !courseId,
  });

  // Fetch Curriculum with Progress for Navigation
  const { data: curriculumData, isLoading } =
    useGetCourseCurriculumWithProgressQuery(courseId as string, {
      skip: !courseId,
    });

  // Flatten the curriculum for easy navigation
  const flatContentList = useMemo(() => {
    if (!curriculumData?.sections) return [];

    const list: {
      sectionId: number;
      contentId: string;
      contentType: "video" | "article" | "quiz" | "pdf" | "embed";
      status?: string;
    }[] = [];

    curriculumData.sections.forEach((section: any) => {
      // Add Lectures
      section.lectures?.forEach((lecture: any) => {
        list.push({
          sectionId: section.sectionId,
          contentId: lecture.lectureId.toString(),
          contentType:
            (lecture.contentType?.toLowerCase() as
              | "video"
              | "article"
              | "pdf"
              | "embed") || "article",
          status: lecture.status,
        });
      });
      // Add Quizzes
      section.quizzes?.forEach((quiz: any) => {
        list.push({
          sectionId: section.sectionId,
          contentId: quiz.quizId.toString(),
          contentType: "quiz",
          status: quiz.status,
        });
      });
    });

    return list;
  }, [curriculumData]);

  // Initialize state from API resume data or fallback to URL params
  const [currentSection, setCurrentSection] = useState<number>(1);
  const [currentContent, setCurrentContent] = useState<string | undefined>(
    undefined,
  );
  const [currentContentType, setCurrentContentType] = useState<
    "video" | "article" | "quiz" | "pdf" | "embed" | undefined
  >(undefined);

  const [isCompleted, setIsCompleted] = useState(false);
  const [isRatingModalOpen, setIsRatingModalOpen] = useState(false);

  // const handleRatingSubmit = async (rating: number) => {
  //   try {
  //     await submitReview({
  //       courseId: courseId as string,
  //       rating,
  //       comment: "", // Sending empty review as we only captured rating
  //     }).unwrap();

  //     // Mark the last lecture complete if needed, although UserCourseContent handles lectures
  //     // We explicitly set course completion here to show certificate
  //     setIsCompleted(true);
  //     setIsRatingModalOpen(false);
  //     toast("success", "Rating submitted successfully!");
  //   } catch (error) {
  //     toast("error", "Failed to submit rating");
  //   }
  // };

  useEffect(() => {
    if (isLoading || !curriculumData) return;

    const sectionParam = searchParams.get("sectionId");
    const contentParam = searchParams.get("contentId");
    const typeParam = searchParams.get("contentType");

    if (sectionParam && contentParam && typeParam) {
      setCurrentSection(Number(sectionParam));
      setCurrentContent(contentParam);
      setCurrentContentType(
        typeParam as "video" | "article" | "quiz" | "pdf" | "embed",
      );
    } else if (!currentContent) {
      // 1. Try to determine resume point from API `resume`
      let targetIndex = -1;

      if (curriculumData.resume) {
        const { lectureId } = curriculumData.resume;
        // The resume object from API primarily tracks lectures
        const resumeIndex = flatContentList.findIndex(
          (item) =>
            item.contentId === lectureId.toString() &&
            item.contentType !== "quiz",
        );
        if (resumeIndex !== -1) targetIndex = resumeIndex;
      }

      // 2. Check if there is a 'COMPLETED' item (Quiz or Lecture) that is FURTHER ahead than the resume point
      // This fixes the issue where completing a quiz doesn't update the 'resume' pointer for lectures

      // reversing to find the last completed item (alternative to findLastIndex)
      const reversedIndex = [...flatContentList]
        .reverse()
        .findIndex((item) => item.status === "COMPLETED");

      const lastCompletedIndex =
        reversedIndex === -1 ? -1 : flatContentList.length - 1 - reversedIndex;

      if (lastCompletedIndex > targetIndex) {
        targetIndex = lastCompletedIndex;
      }

      // 3. Fallback to first item if nothing found
      if (targetIndex === -1 && flatContentList.length > 0) {
        targetIndex = 0;
      }

      // Apply selection
      if (targetIndex !== -1) {
        const target = flatContentList[targetIndex];
        setCurrentSection(target.sectionId);
        setCurrentContent(target.contentId);
        setCurrentContentType(target.contentType);
      }
    }
  }, [
    curriculumData,
    isLoading,
    searchParams,
    flatContentList,
    currentContent,
  ]);

  const handleContentSelect = (
    sectionId: number,
    contentId: string,
    contentType: "video" | "article" | "quiz" | "pdf" | "embed",
  ) => {
    setCurrentSection(sectionId);
    setCurrentContent(contentId);
    setCurrentContentType(contentType);
  };

  // Find index of current content
  const currentIndex = flatContentList.findIndex(
    (item) =>
      item.contentId === currentContent &&
      (!currentContentType || item.contentType === currentContentType),
  );

  const hasNext =
    currentIndex !== -1 && currentIndex < flatContentList.length - 1;
  const hasPrevious = currentIndex > 0;

  const handleNext = () => {
    if (hasNext) {
      const nextItem = flatContentList[currentIndex + 1];
      handleContentSelect(
        nextItem.sectionId,
        nextItem.contentId,
        nextItem.contentType,
      );
      window.scrollTo({
        top: 0,
      });
    } else {
      // Last step
      if (data?.exists) {
        setIsCompleted(true);
        window.scrollTo({
          top: 0,
        });
      } else {
        setIsRatingModalOpen(true);
      }
    }
  };

  const handlePrevious = () => {
    if (hasPrevious) {
      const prevItem = flatContentList[currentIndex - 1];
      handleContentSelect(
        prevItem.sectionId,
        prevItem.contentId,
        prevItem.contentType,
      );
      window.scrollTo({
        top: 0,
      });
    }
  };

  const isLastStep =
    currentIndex !== -1 && currentIndex === flatContentList.length - 1;

  // TODO: Changed
  const breadcrumb = [
    {
      title: "কোর্সসমূহ",
      path: "/user/courses",
    },
    {
      title: decodeURIComponent(filters?.title),
    },
  ];

  // Calculate Section and Lecture Order
  const { sectionOrder, lectureOrder } = useMemo(() => {
    if (!curriculumData?.sections || !currentSection)
      return { sectionOrder: 0, lectureOrder: 0 };

    const sectionIndex = curriculumData.sections.findIndex(
      (s: any) => s.sectionId === currentSection,
    );
    if (sectionIndex === -1) return { sectionOrder: 0, lectureOrder: 0 };

    const section = curriculumData.sections[sectionIndex];
    let lectureOrder = 0;

    if (currentContent && section.lectures) {
      const lectureIndex = section.lectures.findIndex(
        (l: any) => l.lectureId.toString() === currentContent,
      );
      if (lectureIndex !== -1) {
        lectureOrder = lectureIndex + 1;
      }
    }

    return {
      sectionOrder: sectionIndex + 1,
      lectureOrder: lectureOrder,
    };
  }, [curriculumData, currentSection, currentContent]);

  return (
    <UserLayout title="কোর্স" breadcrumb={breadcrumb} hideSidebar>
      {isCompleted ? (
        <CertificateView />
      ) : (
        <section className="container grid lg:grid-cols-5 gap-4 py-10">
          <div className="lg:col-span-2">
            <UserCourseCurriculum
              currentContentId={currentContent}
              currentContentType={currentContentType}
              onContentSelect={handleContentSelect}
            />
          </div>
          <div className="lg:col-span-3">
            <UserCourseContent
              currentSectionId={currentSection}
              currentContentId={currentContent}
              currentContentType={currentContentType}
              onContentChange={handleContentSelect}
              seIsCompleted={setIsCompleted}
              onNext={handleNext}
              onPrevious={hasPrevious ? handlePrevious : undefined}
              isCurriculumLoading={isLoading}
              progress={curriculumData?.progress}
              isLastStep={isLastStep}
              sectionOrder={sectionOrder}
              lectureOrder={lectureOrder}
            />
          </div>
        </section>
      )}

      <CourseRatingModal
        open={isRatingModalOpen}
        handleClose={() => setIsRatingModalOpen(false)}
        courseId={String(courseId)}
        setIsCompleted={setIsCompleted}
      />
    </UserLayout>
  );
}
